// Player <-> server transport for the heartbeat / bundle exchange.
//
// In production the player POSTs to `/api/player/heartbeat`, a Cloudflare
// Pages Function that:
//   - validates the token server-side using the service-role key
//   - signs media URLs at request time
//   - returns an enriched bundle (default + scheduled revisions, with fresh
//     URLs and bundle metadata)
//
// Transport mode is controlled by the build-time env var
// `VITE_PLAYER_TRANSPORT`:
//
//   * `edge` (DEFAULT, production) — only the Pages Function path is used.
//     If the function is unreachable the player surfaces a clear
//     `service_unavailable` error. v2 manifests, which depend on
//     server-signed URLs, are never served from the legacy direct-RPC RPC.
//
//   * `edge-with-rpc-fallback` (dev / migration) — when the Pages Function
//     fails, falls back to the direct Supabase `player_heartbeat` RPC. The
//     fallback bundle is only safe to use for legacy v1 manifests that have
//     URLs baked in; v2 manifests without signed URLs are filtered out.
//
// All errors are returned as data; nothing throws.

import { supabase } from './supabase';
import { normalizeBundle, type PlayerBundleV1 } from '@/types/schedule';
import type { Manifest } from '@/types/manifest';
import type { PlayEvent } from './play-events';

export type HeartbeatSource = 'edge' | 'rpc';

export type PlayerTransportMode = 'edge' | 'edge-with-rpc-fallback';

export type HeartbeatErrorCode =
  | 'missing_token'
  | 'invalid_token'
  | 'revoked'
  | 'screen_not_found'
  | 'service_unavailable'
  | 'rpc_failed'
  | 'misconfigured'
  | (string & {});

export interface HeartbeatPayload {
  screenId: string;
  playerToken: string;
  playerName?: string | null;
  cachedRevision?: string | null;
  cacheStatus?: string | null;
  userAgent?: string | null;
  storageEstimate?: { quota?: number; usage?: number } | null;
  lastError?: string | null;
  selectedRevision?: string | null;
  scheduleState?: string | null;
  bundleVersion?: string | null;
  /** Buffered Proof-of-Play events to flush. Capped client-side at 100/batch. */
  playEvents?: PlayEvent[];
}

export interface HeartbeatResponse {
  /** Which transport handled the request, for diagnostics. */
  source: HeartbeatSource;
  /** Server-reported error code (e.g. invalid_token, revoked, service_unavailable). */
  error?: HeartbeatErrorCode;
  playerId: string | null;
  activeRevision: string | null;
  manifest: Manifest | null;
  bundle: PlayerBundleV1 | null;
  /** True when this response carried a v2 manifest with no signed URL — i.e. the bundle is unsafe for cold play-out. */
  unsignedV2?: boolean;
  /** Proof-of-Play event IDs the server inserted. The client deletes these from the local buffer. */
  acceptedPlayEventIds?: string[];
  /** Proof-of-Play event IDs the server rejected (validation failure / dedupe). The client drops these to prevent poison-pill loops. */
  rejectedPlayEventIds?: string[];
  /**
   * Per-player runtime settings returned by the heartbeat. Currently just
   * the Proof-of-Play toggle; absent on the legacy RPC transport.
   */
  playerSettings?: PlayerSettings;
}

export interface PlayerSettings {
  proofOfPlayEnabled: boolean;
}

const EDGE_PATH = '/api/player/heartbeat';
const EDGE_TIMEOUT_MS = 8000;
const EDGE_DISABLE_KEY = 'signage:edge-disabled-until';
const EDGE_BACKOFF_MS = 2 * 60_000;

function readTransportMode(): PlayerTransportMode {
  // Read from import.meta.env at runtime.
  //
  // Defaults:
  //   - dev (`vite dev`): edge-with-rpc-fallback
  //   - production build/preview: edge (strict)
  //
  // This keeps new local projects usable before Pages Functions are wired up,
  // while preserving strict production behaviour.
  try {
    const env =
      (import.meta as unknown as { env?: Record<string, string | undefined> })?.env ?? {};
    const mode = env.VITE_PLAYER_TRANSPORT;
    if (mode === 'edge-with-rpc-fallback') return 'edge-with-rpc-fallback';
    if (mode === 'edge') return 'edge';
    if (env.DEV === 'true') return 'edge-with-rpc-fallback';
  } catch {
    /* ignore — Node test env etc. */
  }
  return 'edge';
}

/**
 * Send a heartbeat. The transport strategy depends on
 * `VITE_PLAYER_TRANSPORT`:
 *
 *   * edge (default, production) — only the Pages Function. A failure
 *     yields `error: 'service_unavailable'`; the player keeps any cached
 *     revision and reports the failure as diagnostic.
 *   * edge-with-rpc-fallback (dev / migration) — falls back to the direct
 *     Supabase RPC, but only returns its bundle if no v2 manifest is
 *     missing a URL. v2 manifests must always come from the edge.
 */
export async function sendHeartbeat(payload: HeartbeatPayload): Promise<HeartbeatResponse> {
  const mode = readTransportMode();
  const useEdge = isEdgeAvailable();
  if (useEdge) {
    const edge = await tryEdge(payload);
    if (edge) return edge;
    markEdgeUnavailable();
  }

  if (mode === 'edge') {
    return {
      source: 'edge',
      error: 'service_unavailable',
      playerId: null,
      activeRevision: null,
      manifest: null,
      bundle: null,
    };
  }

  // dev fallback
  const rpc = await tryRpc(payload);
  if (rpc.bundle && bundleHasUnsignedV2(rpc.bundle)) {
    return { ...rpc, unsignedV2: true, bundle: filterUnsignedFromBundle(rpc.bundle) };
  }
  return rpc;
}

function isEdgeAvailable(): boolean {
  try {
    const until = Number(localStorage.getItem(EDGE_DISABLE_KEY) ?? '0');
    return !until || Date.now() > until;
  } catch {
    return true;
  }
}

function markEdgeUnavailable(): void {
  try {
    localStorage.setItem(EDGE_DISABLE_KEY, String(Date.now() + EDGE_BACKOFF_MS));
  } catch {
    /* ignore — private mode etc. Player still functions, just with extra edge hits. */
  }
}

async function tryEdge(payload: HeartbeatPayload): Promise<HeartbeatResponse | null> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), EDGE_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetch(EDGE_PATH, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
        signal: ctrl.signal,
      });
    } finally {
      clearTimeout(timer);
    }
    // 404 / 405 = function not deployed. Fall through (the caller decides
    // whether that's allowed in this build mode).
    if (res.status === 404 || res.status === 405) return null;
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (!data) return null;
    return {
      source: 'edge',
      error: typeof data.error === 'string' ? (data.error as HeartbeatErrorCode) : undefined,
      playerId: typeof data.playerId === 'string' ? data.playerId : null,
      activeRevision: typeof data.activeRevision === 'string' ? data.activeRevision : null,
      manifest: (data.manifest as Manifest | null) ?? null,
      bundle: normalizeBundle(data.bundle),
      acceptedPlayEventIds: extractIdArray(data.acceptedPlayEventIds),
      rejectedPlayEventIds: extractIdArray(data.rejectedPlayEventIds),
      playerSettings: extractPlayerSettings(data.playerSettings),
    };
  } catch {
    return null;
  }
}

function extractPlayerSettings(value: unknown): PlayerSettings | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const obj = value as Record<string, unknown>;
  if (typeof obj.proofOfPlayEnabled !== 'boolean') return undefined;
  return { proofOfPlayEnabled: obj.proofOfPlayEnabled };
}

function extractIdArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: string[] = [];
  for (const v of value) {
    if (typeof v === 'string' && v.length > 0) out.push(v);
  }
  return out;
}

async function tryRpc(payload: HeartbeatPayload): Promise<HeartbeatResponse> {
  const { data, error } = await supabase.rpc('player_heartbeat', {
    _screen_id: payload.screenId,
    _player_token: payload.playerToken,
    _player_name: payload.playerName ?? null,
    _cached_revision: payload.cachedRevision ?? null,
    _cache_status: payload.cacheStatus ?? null,
    _user_agent: payload.userAgent ?? null,
    _storage_estimate: payload.storageEstimate ?? null,
    _last_error: payload.lastError ?? null,
  });
  if (error) {
    return {
      source: 'rpc',
      error: error.message ?? 'rpc_failed',
      playerId: null,
      activeRevision: null,
      manifest: null,
      bundle: null,
    };
  }
  const obj = (data ?? {}) as Record<string, unknown>;
  return {
    source: 'rpc',
    error: typeof obj.error === 'string' ? (obj.error as HeartbeatErrorCode) : undefined,
    playerId: typeof obj.playerId === 'string' ? obj.playerId : null,
    activeRevision: typeof obj.activeRevision === 'string' ? obj.activeRevision : null,
    manifest: (obj.manifest as Manifest | null) ?? null,
    bundle: normalizeBundle(obj.bundle),
  };
}

interface MaybeManifest {
  schemaVersion?: number;
  playlist?: Array<{ url?: string }>;
}

function manifestNeedsSigning(manifest: unknown): boolean {
  if (!manifest || typeof manifest !== 'object') return false;
  const m = manifest as MaybeManifest;
  if (m.schemaVersion !== 2) return false;
  const items = Array.isArray(m.playlist) ? m.playlist : [];
  return items.some((it) => !it || typeof it.url !== 'string' || it.url.length === 0);
}

function bundleHasUnsignedV2(bundle: PlayerBundleV1): boolean {
  if (manifestNeedsSigning(bundle.defaultManifest)) return true;
  for (const s of bundle.scheduled) {
    if (manifestNeedsSigning(s.manifest)) return true;
  }
  return false;
}

/**
 * Strip revisions from the bundle whose v2 manifests have no signed URLs.
 * Returns null when the result has nothing playable left.
 */
function filterUnsignedFromBundle(bundle: PlayerBundleV1): PlayerBundleV1 | null {
  const defaultBad = manifestNeedsSigning(bundle.defaultManifest);
  const scheduled = bundle.scheduled.filter((s) => !manifestNeedsSigning(s.manifest));
  const filtered: PlayerBundleV1 = {
    ...bundle,
    defaultRevisionId: defaultBad ? null : bundle.defaultRevisionId,
    defaultManifest: defaultBad ? null : bundle.defaultManifest,
    scheduled,
  };
  if (!filtered.defaultManifest && filtered.scheduled.length === 0) return null;
  return filtered;
}

// Exposed for tests + diagnostics UI.
export const __TEST = {
  manifestNeedsSigning,
  bundleHasUnsignedV2,
  filterUnsignedFromBundle,
  readTransportMode,
};
